import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Button,
  Typography,
  Grid,
  InputAdornment,
  CircularProgress,
  Alert,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { currencyService, popularCurrencies, formatCurrency } from '../services/api';

const CurrencyConverter = ({ showTitle = false }) => {
  const [amount, setAmount] = useState('1');
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [toCurrency, setToCurrency] = useState('EUR');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const theme = useTheme();

  // Load all currencies on component mount
  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        const response = await currencyService.getCurrencies();
        if (response.success) {
          setCurrencies(response.currencies);
        }
      } catch (error) {
        console.error('Error loading currencies:', error);
        // Fallback to popular currencies if API fails
        setCurrencies(popularCurrencies.map(c => ({ code: c.code, rate: 1 })));
      }
    };
    
    loadCurrencies();
  }, []);

  // Auto-convert when amount or currencies change
  useEffect(() => {
    if (amount && fromCurrency && toCurrency && !loading) {
      handleConvert();
    }
  }, [amount, fromCurrency, toCurrency]);

  const handleConvert = async () => {
    if (!amount || !fromCurrency || !toCurrency) {
      setError('Please fill in all fields');
      return;
    }

    if (isNaN(amount) || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await currencyService.convertCurrency(
        parseFloat(amount),
        fromCurrency,
        toCurrency
      );

      if (response.success) {
        setResult(response);
      } else {
        setError(response.error || 'Conversion failed');
      }
    } catch (error) {
      setError(error.message || 'Failed to convert currency');
    } finally {
      setLoading(false);
    }
  };

  const handleSwapCurrencies = () => {
    const temp = fromCurrency;
    setFromCurrency(toCurrency);
    setToCurrency(temp);
  };

  const handleQuickAmount = (value) => {
    setAmount(value.toString());
  };

  const getCurrencyDisplay = (code) => {
    const currency = popularCurrencies.find(c => c.code === code);
    return currency ? `${currency.flag} ${code} - ${currency.name}` : code;
  };

  const getAvailableCurrencies = () => {
    // Combine popular currencies with all available currencies
    const allCodes = new Set();
    const combined = [];
    
    // Add popular currencies first
    popularCurrencies.forEach(currency => {
      if (!allCodes.has(currency.code)) {
        combined.push({
          code: currency.code,
          name: currency.name,
          flag: currency.flag,
          popular: true
        });
        allCodes.add(currency.code);
      }
    });
    
    // Add other currencies
    currencies.forEach(currency => {
      if (!allCodes.has(currency.code)) {
        const popularCurrency = popularCurrencies.find(p => p.code === currency.code);
        combined.push({
          code: currency.code,
          name: popularCurrency?.name || currency.code,
          flag: popularCurrency?.flag || '',
          popular: false
        });
        allCodes.add(currency.code);
      }
    });
    
    return combined.sort((a, b) => {
      if (a.popular && !b.popular) return -1;
      if (!a.popular && b.popular) return 1;
      return a.code.localeCompare(b.code);
    });
  };

  return (
    <Card
      elevation={8}
      sx={{
        borderRadius: 3,
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`,
        backdropFilter: 'blur(20px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
      }}
    >
      <CardContent sx={{ p: 4 }}>
        {showTitle && (
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 600,
              mb: 3,
              textAlign: 'center',
              color: theme.palette.text.primary,
            }}
          >
            Currency Converter
          </Typography>
        )}

        <Grid container spacing={3}>
          {/* Amount Input */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '1.2rem',
                  fontWeight: 500,
                },
              }}
            />
            
            {/* Quick Amount Buttons */}
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {[1, 10, 100, 1000].map((value) => (
                <Chip
                  key={value}
                  label={value}
                  onClick={() => handleQuickAmount(value)}
                  variant={amount === value.toString() ? 'filled' : 'outlined'}
                  size="small"
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.light,
                      color: 'white',
                    },
                  }}
                />
              ))}
            </Box>
          </Grid>

          {/* From Currency */}
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              select
              label="From"
              value={fromCurrency}
              onChange={(e) => setFromCurrency(e.target.value)}
            >
              {getAvailableCurrencies().map((currency) => (
                <MenuItem key={currency.code} value={currency.code}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography component="span">
                      {currency.flag} {currency.code}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {currency.name}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Swap Button */}
          <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={handleSwapCurrencies}
              sx={{
                minWidth: 56,
                height: 56,
                borderRadius: '50%',
                border: `2px solid ${theme.palette.primary.main}`,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  transform: 'rotate(180deg)',
                },
                transition: 'all 0.3s ease-in-out',
              }}
            >
              <SwapHorizIcon />
            </Button>
          </Grid>

          {/* To Currency */}
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              select
              label="To"
              value={toCurrency}
              onChange={(e) => setToCurrency(e.target.value)}
            >
              {getAvailableCurrencies().map((currency) => (
                <MenuItem key={currency.code} value={currency.code}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography component="span">
                      {currency.flag} {currency.code}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {currency.name}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Convert Button */}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleConvert}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <TrendingUpIcon />}
              sx={{
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                '&:hover': {
                  background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                },
              }}
            >
              {loading ? 'Converting...' : 'Convert Currency'}
            </Button>
          </Grid>

          {/* Error Display */}
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}

          {/* Result Display */}
          {result && (
            <Grid item xs={12}>
              <Card
                elevation={2}
                sx={{
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 1 }}>
                    {formatCurrency(result.convertedAmount, result.toCurrency)}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    {formatCurrency(result.amount, result.fromCurrency)} = {formatCurrency(result.convertedAmount, result.toCurrency)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Exchange Rate: 1 {result.fromCurrency} = {result.exchangeRate} {result.toCurrency}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Last updated: {new Date(result.lastUpdate).toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CurrencyConverter;
